import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { InputRef } from "antd";
import cn from "classnames";
import { useIntl } from "react-intl";

import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";
import geoService, {
  convertCoordinatesToLongLat,
} from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, AutoCompleteAddress, Button, Labeled, SelectField } from "@mapmycustomers/ui";

import poweredByGoogle from "@app/assets/google/poweredByGoogle.png";
import LocatedEntityAssociation from "@app/component/associations/LocatedEntityAssociation";
import ButtonLink from "@app/component/ButtonLink";
import {
  isAddressAreaSearchQuery,
  isCoordinatesAreaSearchQuery,
  isEntityAreaSearchQuery,
} from "@app/component/input/AreaSearchInput/utils/assert";
import GeoPointSortType, {
  getGeopointSortTypeDisplayName,
} from "@app/component/RecordList/components/CustomizeMapSortModal/enum/GeoPointSortType";
import styles from "@app/component/RecordList/components/CustomizeMapSortModal/LocationSort.module.scss";
import messages from "@app/component/RecordList/components/CustomizeMapSortModal/messages";
import AreaSearchMode from "@app/enum/AreaSearchMode";
import { showEntityView } from "@app/store/entityView/actions";
import { geocodeAddress, reverseGeocodeAddress } from "@app/store/location/actions";
import {
  AddressAreaSearchQuery,
  AreaSearchQuery,
  EntityAreaSearchQuery,
} from "@app/types/filters/AreaSearchQuery";
import { FULL_ADDRESS } from "@app/util/consts";
import { entityToAddressLines } from "@app/util/formatters";

const TYPES: string[] = ["geocode"];

interface Props {
  className?: string;
  mode?: GeoPointSortType;
  onGeocodeAddress: typeof geocodeAddress;
  onModeChange?: (mode: GeoPointSortType) => void;
  onQueryChange?: (query?: AreaSearchQuery) => void;
  onReverseGeocodeAddress: typeof reverseGeocodeAddress;
  onShowEntityView: typeof showEntityView;
  query?: AreaSearchQuery;
}

const LocationSort = ({
  className,
  mode,
  onGeocodeAddress,
  onModeChange,
  onQueryChange,
  onReverseGeocodeAddress,
  onShowEntityView,
  query,
}: Props) => {
  const intl = useIntl();
  const locationSearchRef = useRef<InputRef>(null);

  const [entityPopupVisible, showEntityPopup, hideEntityPopup] = useBoolean();
  const [locationText, setLocationText] = useState<string>("");
  const [showUserLocationWarning, setShowUserLocationWarning] = useState(false);

  const showLocation =
    !!locationText && !showUserLocationWarning && mode === GeoPointSortType.CURRENT_USER_LOCATION;

  const geopointOptions = useMemo(
    () =>
      Object.values(GeoPointSortType).map((field) => ({
        label: getGeopointSortTypeDisplayName(intl, field),
        value: field,
      })),
    [intl]
  );

  const setCurrentUserLocation = useCallback(
    async (newMode: GeoPointSortType) => {
      let coordinates: LongLat | undefined;
      try {
        const position = await geoService.getCurrentPosition();
        coordinates = convertCoordinatesToLongLat(position.coords);
        setShowUserLocationWarning(false);
      } catch {
        setShowUserLocationWarning(true);
      }

      if (!isDefined(coordinates)) {
        return; // can't proceed without coordinates
      }

      const address = await new Promise<string>((resolve) =>
        onReverseGeocodeAddress({
          callback: (result: GeocodeResult) => resolve(result.address.formattedAddress ?? ""),
          coordinates: coordinates!, // we just checked it's defined
          failureCallback: () => resolve(coordinates!.join(", ")),
        })
      );
      if (newMode === GeoPointSortType.CURRENT_USER_LOCATION) {
        onQueryChange?.({ address, coordinates, mode: newMode });
      }
    },
    [onQueryChange, onReverseGeocodeAddress, setShowUserLocationWarning]
  );

  const handleAddressChange = useCallback(
    (address?: Address) => {
      if (!address) {
        return;
      }

      onGeocodeAddress({
        address,
        callback: (result: GeocodeResult) => {
          onQueryChange?.({
            address: getFormattedAddressForUi(address),
            coordinates: result.geoPoint?.coordinates,
            mode: GeoPointSortType.ADDRESS,
          });
        },
        failureCallback: () => {
          onQueryChange?.({
            address: getFormattedAddressForUi(address),
            mode: GeoPointSortType.ADDRESS,
          });
        },
      });
    },
    [onGeocodeAddress, onQueryChange]
  );

  const handleSelectEntity = useCallback(
    (entity: EntityWithLocation) => {
      onQueryChange?.({ entity, mode: GeoPointSortType.ENTITY });
    },
    [onQueryChange]
  );

  const handleEntityViewClick = useCallback(() => {
    if (isEntityAreaSearchQuery(query)) {
      onShowEntityView({ entityId: query.entity.id, entityType: query.entity.entity });
    }
  }, [onShowEntityView, query]);

  const handleModeClick = useCallback(
    async (value: GeoPointSortType) => {
      const newMode: GeoPointSortType = value;
      if (mode === newMode) {
        return;
      }
      onModeChange?.(newMode);
      setLocationText("");
      onQueryChange?.(undefined);

      if (newMode === AreaSearchMode.ENTITY) {
        showEntityPopup();
      } else {
        hideEntityPopup();
      }

      if (newMode === AreaSearchMode.CURRENT_USER_LOCATION) {
        await setCurrentUserLocation(newMode);
      }
    },
    [onModeChange, onQueryChange, showEntityPopup, mode, hideEntityPopup, setCurrentUserLocation]
  );

  useEffect(() => {
    if (query?.mode) {
      onModeChange?.(query.mode as GeoPointSortType);
    }
    if (isCoordinatesAreaSearchQuery(query)) {
      if (query.address) {
        setLocationText(query.address);
      } else {
        setLocationText(query.coordinates.join(", "));
        onReverseGeocodeAddress({
          callback: (result: GeocodeResult) => {
            setLocationText(result.address.formattedAddress ?? "");
          },
          coordinates: query.coordinates,
        });
      }
    } else if (isAddressAreaSearchQuery(query)) {
      setLocationText(query.address);
    } else if (isEntityAreaSearchQuery(query)) {
      const { line1, line2, line3 } = entityToAddressLines(query.entity, FULL_ADDRESS, {
        locationWhenNoAddress: true,
      });
      const text = [line1, line2, line3].filter((line) => !!line).join(", ");
      setLocationText(text);
    }
  }, [onModeChange, onReverseGeocodeAddress, query]);

  useEffect(() => {
    if (mode === GeoPointSortType.CURRENT_USER_LOCATION && !query) {
      setCurrentUserLocation(GeoPointSortType.CURRENT_USER_LOCATION);
    }
  }, [mode, query, setCurrentUserLocation]);

  useEffect(() => {
    if (mode === GeoPointSortType.ADDRESS) {
      locationSearchRef.current?.focus();
    }
  }, [mode, locationSearchRef]);

  return (
    <div className={cn(styles.container, className)}>
      <SelectField<GeoPointSortType>
        className={styles.select}
        label={intl.formatMessage(messages.where)}
        onChange={handleModeClick}
        options={geopointOptions}
        value={mode}
      />

      {showUserLocationWarning && mode === GeoPointSortType.CURRENT_USER_LOCATION && (
        <Alert
          action={
            <ButtonLink
              className={styles.helpLink}
              href="https://hs.mapmycustomers.me/en/support/how-to-share-your-location-with-your-web-browser"
              target="_blank"
            >
              {intl.formatMessage(messages.locationErrorHelp)}
            </ButtonLink>
          }
          message={intl.formatMessage(messages.locationError)}
          showIcon
          type="warning"
        />
      )}

      {showLocation && (
        <div className={cn(styles.current)}>
          <span>{locationText}</span>
        </div>
      )}

      {mode === GeoPointSortType.ADDRESS && (
        <div className={cn({ [styles.hasFeedback]: !(query as AddressAreaSearchQuery)?.address })}>
          <AutoCompleteAddress
            allowClear
            className={className}
            extra={<img alt="powered by Google" src={poweredByGoogle} />}
            label={intl.formatMessage(messages.addressLabel)}
            labelClassName={styles.addressLabel}
            onChange={handleAddressChange}
            onReverseGeocodeAddress={onReverseGeocodeAddress}
            placePredictionTypes={TYPES}
            ref={locationSearchRef}
            required
            value={{ address: (query as AddressAreaSearchQuery)?.address ?? "" }}
          />
          {!(query as AddressAreaSearchQuery)?.address && (
            <div className={styles.feedback}>{intl.formatMessage(messages.addLocation)}</div>
          )}
        </div>
      )}

      {mode === GeoPointSortType.ENTITY && (
        <>
          <Labeled label={intl.formatMessage(messages.entityLabel)} required>
            <div className={styles.entity}>
              {(query as EntityAreaSearchQuery)?.entity && (
                <div className={styles.entityInfo}>
                  <div className={styles.entityName}>
                    <ButtonLink onClick={handleEntityViewClick}>
                      {(query as EntityAreaSearchQuery).entity.name}
                    </ButtonLink>
                  </div>

                  <div className={styles.entityAddress}>{locationText}</div>
                </div>
              )}

              <div>
                <Button disabled={entityPopupVisible} onClick={showEntityPopup}>
                  {intl.formatMessage(
                    (query as EntityAreaSearchQuery)?.entity
                      ? messages.changeRecord
                      : messages.selectRecord
                  )}
                </Button>
              </div>

              <LocatedEntityAssociation
                onHide={hideEntityPopup}
                onSelect={handleSelectEntity}
                selectedEntityId={(query as EntityAreaSearchQuery)?.entity?.id}
                visible={entityPopupVisible}
              />
            </div>
          </Labeled>
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onGeocodeAddress: geocodeAddress,
  onReverseGeocodeAddress: reverseGeocodeAddress,
  onShowEntityView: showEntityView,
};

export default connect(null, mapDispatchToProps)(LocationSort);
